/**
 * Utility functions for Nova AI Chat
 */

import { Engine, QueryTypeEnum, SQLDialect, Query } from '@/lib/engine';
import { SQLQueryResponse, SchemaResponse } from './types';

/**
 * Execute SQL Query
 * 
 * Executes an SQL query against the Nova engine.
 */
export async function executeSqlQuery(query: string, engine: Engine): Promise<SQLQueryResponse> {
  
  try {
    // Get the first table or any table provided by novastore from context
    const tables = await engine.listRegisteredTables();
    if (!tables || tables.length === 0) {
      return {
        success: false,
        error: 'No tables available',
        query // Include the query in the error response
      };
    }
    
    // Use the first table
    const activeTable = tables[0];
    
    // Get the table view
    const view = await engine.getTableView(activeTable);
    
    // Create a SQL query
    const sqlQuery: Query = {
      type: QueryTypeEnum.SQL,
      options: {
        sql: query,
        dialect: SQLDialect.DUCKDB
      }
    };
    
    // Transform and materialize the data
    const transformed = await engine.transform(view, sqlQuery);
    const result = await engine.materialize(transformed);
    
    if (!result || !result.table) {
      return {
        success: false,
        error: 'Failed to execute query',
        query // Include the query in the error response
      };
    }
    
    // Extract column information
    const schema = result.table.schema;
    const columns = [];
    const data = [];
    
    // Get column names and types from the schema using type assertion
    // This is necessary because the TableSchema type might not have the fields property exposed
    const schemaWithFields = schema as any;
    if (schemaWithFields && schemaWithFields.fields) {
      for (let i = 0; i < schemaWithFields.fields.length; i++) {
        const field = schemaWithFields.fields[i];
        if (field) {
          columns.push({
            name: field.name,
            type: field.type.toString()
          });
        }
      }
    }
    
    // Extract data from the table
    for (let i = 0; i < result.table.numRows; i++) {
      const row: Record<string, any> = {};
      if (schemaWithFields && schemaWithFields.fields) {
        for (let j = 0; j < schemaWithFields.fields.length; j++) {
          const field = schemaWithFields.fields[j];
          if (field) {
            const column = result.table.getChildAt(j);
            if (column) {
              row[field.name] = column.get(i);
            }
          }
        }
      }
      data.push(row);
    }
    
    return {
      success: true,
      data,
      columns,
      rowCount: result.table.numRows,
      message: 'Query executed successfully',
      query // Include the query in the success response
    };
  } catch (error) {
    console.error('Error executing SQL query:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error),
      query // Include the query in the error response
    };
  }
}

/**
 * Get Schema
 * 
 * Retrieves schema information from the Nova engine using SQL queries.
 */
export async function getSchema(engine: Engine): Promise<SchemaResponse> {
  
  try {
    // List all registered tables
    const tables = await engine.listRegisteredTables();
    
    if (!tables || tables.length === 0) {
      return {
        success: false,
        error: 'No tables found'
      };
    }
    
    // Create tableSchemas directly from the tables returned by listRegisteredTables
    const tableSchemas = tables.map(table => {
      // Extract column information from the table schema
      const columns = table.schema.columns.map(column => ({
        name: column.name,
        type: column.type.toString(),
        nullable: column.nullable,
        // We don't have default and maxLength information directly from the schema
        // These would typically come from the SQL query we were using before
      }));
      
      return {
        name: table.path || 'unknown',
        columns
      };
    });
    
    const schemaResponse = {
      success: true,
      tables: tableSchemas
    };
    
    return schemaResponse;
  } catch (error) {
    console.error('Error retrieving schema information:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : String(error)
    };
  }
} 